import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ContentsHelper} from "../../../helpers/contents.helper";
import {LangChangeEvent, TranslateService} from "@ngx-translate/core";
import {combineLatest, Subscription} from "rxjs";
import {ListingConfiguration} from "../../../models/listing-configuration";
import {DetailService} from "../../../services/details/detail.service";
import {ListingService} from '../../../services/listing/listing.service';
import {HelperService} from '../../../services/helperService';
import {Ng4LoadingSpinnerService} from "ng4-loading-spinner";
import {NavigationService} from "../../../services/navigation/navigation.service";
import {AlertService} from "../../../helpers/alert/alert.service";
import SettingsLegacy = DataTables.SettingsLegacy;
import StateReturnModel = DataTables.StateReturnModel;
import {DataTableDirective} from "angular-datatables";


/**
 * Class ListingComponent
 */
@Component({
	selector: "tig-gauntlet-listing",
	templateUrl: "./listing.template.html"
})
export class ListingComponent implements OnInit, OnDestroy
{

	@ViewChild(DataTableDirective, {static: false})
	datatableElement: DataTableDirective;

	/**
	 * Attributes
	 */
	dt_options: DataTables.Settings = {};
	title;
	table_show = true;
	listing_config: ListingConfiguration;
	contents_helper: ContentsHelper;
	private subscriptions: Subscription[] = [Subscription.EMPTY];
    private route_menu: string;
    private route_submenu: string;
	private nested_listing: boolean;
	notification: string;
    listing_id: string;
    bulkinsert = false;
	reduced_detail = false;

    /**
	 * Constructor
	 *
	 * @param {Router} router
	 * @param {ActivatedRoute} activate_route
	 * @param {ChangeDetectorRef} change_detector_ref
	 * @param {TranslateService} translate_service
	 * @param detail_service
	 * @param listing_service
	 * @param helper_service
	 * @param {Ng4LoadingSpinnerService} spinner_service
	 * @param {NavigationService} navigation
	 * @param alert_service
	 */
	constructor
	(
		private router: Router,
		private activate_route: ActivatedRoute,
		private change_detector_ref: ChangeDetectorRef,
		private translate_service: TranslateService,
		private detail_service: DetailService,
		private listing_service: ListingService,
        private helper_service: HelperService,
		private spinner_service: Ng4LoadingSpinnerService,
        private navigation: NavigationService,
		private alert_service: AlertService
	) {} // end Constructor

	/**
	 * LifecycleHook > ngOnInit
	 */
	ngOnInit(): void
	{

        this.route_submenu = this.activate_route.snapshot.params["route_submenu"];

        this.route_menu = this.activate_route.snapshot.parent.params["route_menu"];

        // Reagiere auf Aenderung der URL-Parameter
        this.subscriptions.push(
            combineLatest([this.activate_route.params, this.activate_route.parent.params])
            .subscribe(
            ARR_values =>
			{

				this.notification = null;
                // Hole die genauen Routen
                this.route_submenu = ARR_values[0]["route_submenu"];
                this.route_menu = ARR_values[1]["route_menu"];
				this.listing_id = ARR_values[0]["listing_id"];

                // Hole Konfiguration der Tabelle
                this.listing_config = this.activate_route.snapshot.data.OBJ_config;

                this.bulkinsert = this.navigation.hasBulkinsert(this.route_menu, this.route_submenu);

				// Auf rahmenlose Detailansicht (fuer eingebundene iFrames) weiterleiten
				this.reduced_detail = this.navigation.isReducedDetail(this.route_menu, this.route_submenu);

                this.title = this.translate_service.instant(this.listing_config.title?this.listing_config.title:this.route_submenu);

                // Lade Daten der Tablle
                this.contents_helper = this.activate_route.snapshot.data.OBJ_contents_helper;

                // Die Listingdaten sind entweder unter dem Namen der Route zu finden, oder unter dem in der Config hinterlegten Namen
                this.fillTableWithData(false);

                // Befinden wir uns in verschachteltem Listing? In dem Fall muessen routen etwas anders gebaut werden
				this.nested_listing = this.activate_route.snapshot.data["nested_listing"];

				// Soll eine Info fuer den User angezeigt werden?
				if(this.listing_config.notification)
				{
					this.notification = this.listing_config.notification;
				} // end if

            }
        ));

        this.listenToLanguageChange();

	} // end LifecycleHook ngOnInit


	/**
	 * LifecycleHook > ngOnDestroy
	 */
	ngOnDestroy()
	{

		// Unsubscribe from all subscriptions to prevent memory leak
		this.subscriptions.forEach(
			(OBJ_subscription: Subscription) =>
			{
				OBJ_subscription.unsubscribe();
			}
		);

	} // end LifecycleHook ngOnDestroy


	/**
	 * Function > listenToLanguageChange
	 *
	 * @desc Passt Benennung der tabellenspalten an, wenn sich Sprache aendert
	 */
	listenToLanguageChange()
	{
		let columns = this.listing_config.columns;
		this.subscriptions.push(this.translate_service.onLangChange.subscribe((langchange_event: LangChangeEvent) => {

			// set new translate file
			delete this.dt_options["oLanguage"];
			this.dt_options.language = {url: "assets/i18n/datatables/" + this.translate_service.currentLang + ".json"};

			// translate column titles
			delete this.dt_options["aoColumns"];
			this.dt_options.columns.forEach((column_config) =>
			{
				let data = column_config.data.toString().toLowerCase(),
					column = columns.filter(obj =>  (typeof obj.data != 'undefined' && obj.data === data)).shift(),
					translate_string = (typeof column.label !== "undefined") ? column.label : column.data,
					title = this.translate_service.instant(translate_string);

				column_config["sTitle"] = title;
				column_config["title"] = title;
			});

			// render table again
			this.rerenderTable();

		}));

	} // end function listenToLanguageChange


    /**
     * Function > rowClickHandler
	 * @desc Forward to detail view on click
	 * @param {object} data
	 * @return void
     */
	rowClickHandler(data: any): void
	{

		// save URL of the detailed data in the detail service to access it in detail view
        this.detail_service.STR_detailurl = this.contents_helper.getLinkToSelf(data);

		// save classname to load config depending on it
        let classname = this.contents_helper.getValue("classname", data);

		let config = this.navigation.getDetailconfigRouteByClassname(this.route_menu, this.route_submenu, classname)

		// Gib die Parameter jedem Request mit
		let query_params = {"queryParams": {"config": config}}

		// verschachteltes Listing
		if(this.nested_listing)
		{
			return this.navigateNestedListing(data, query_params)
		}

		// Detailansicht in reduziertem Rahmen (ohne Speicherbutton...)
		if(this.reduced_detail)
		{
			return this.navigateReducedDetail(data, query_params)
		}

		// Normale Detailansicht
		this.router.navigate([
			this.route_menu,
			this.route_submenu,
			this.contents_helper.findId(data)],
			query_params
		);

    } // end function rowClickHandler


	navigateNestedListing(data, query_params)
	{

		let route = [
			this.route_menu,
			"listing",
			this.route_submenu
		]

		// wenn listing_id schon gesetzt ist befinden wir uns schon in verschachteltem Listing - naechster Stop: Detailansicht
		if(this.listing_id)
		{
			route.push(this.listing_id)
		}

		route.push(this.contents_helper.findId(data));

		this.router.navigate(
			route,
			query_params);
	}

	navigateReducedDetail(data, query_params)
	{
		this.router.navigate([
			this.route_menu,
			this.route_submenu,
			"reduced",
			this.contents_helper.findId(data)],
			query_params
		);
	}


	/**
	 * Function > fillTableWithData
	 * @desc Holt Daten fuer andere Tabelle
	 */
	fillTableWithData(BOO_refresh: boolean)
	{

		// get configuration for the table
		let ARR_columns_config = this.listing_config.columns;

        // Sortieren erlaubt?
		let BOO_orderable = true;
		if(this.listing_config.orderable !== null)
		{
            BOO_orderable = this.listing_config.orderable;
		} // end if

        let ARR_order_config = [];

		let OBJ_menuitem_config = this.navigation.getMenuitemConfigByRoute(this.route_menu, this.route_submenu);

		// initial sorting given (navigation.json)
        if(OBJ_menuitem_config && OBJ_menuitem_config._links.values.sort)
        {

            for (let INT_counter = 0; INT_counter < ARR_columns_config.length; INT_counter++) {

                const OBJ_column = ARR_columns_config[INT_counter];

                // the column is orderable and the order_by-param matches either the sort_by-field or the title-field
                if(OBJ_column.orderable !== false &&
                    (
                        OBJ_column.sort_by == OBJ_menuitem_config._links.values.sort.sort_by ||
                        OBJ_column.data == OBJ_menuitem_config._links.values.sort.sort_by
                    )
                )
                {
                    // found what we are looking for, break the loop
                    ARR_order_config[0] = INT_counter;
                    ARR_order_config[1] = OBJ_menuitem_config._links.values.sort.order;
                    break;
                } // end if

            } // end for

        } // end if

		// add renderer to each column
		this.helper_service.addRenderer(ARR_columns_config);

		// Ausnahmefaelle, fuer die Suche clientseitig durchgefuehrt wird
		if (OBJ_menuitem_config && OBJ_menuitem_config._links.values.client_search)
		{

            // table configuration
            this.dt_options = {
                data: this.contents_helper.getListingData(),
                columns: ARR_columns_config,
                order: ARR_order_config,
                // Speichere den State des Datatables
                stateSave: true,
                // setzte State (Suchtext, order...) in localstorage
                stateSaveCallback:(settings: SettingsLegacy, data: StateReturnModel) => this.setTableState(data),
                // Hole State des Datatables, falls gesetzt
                stateLoadCallback: settings => this.getTableState(),
                ordering: BOO_orderable,
                searching: (this.listing_config.searchable !== null)?this.listing_config.searchable:true,
                language: {
                    url: "assets/i18n/datatables/" + this.translate_service.currentLang + ".json"
                },
                lengthMenu: [10,25, 50, 100],
                pageLength: 25,
				responsive: true,
                // click event for the row
                rowCallback: (OBJ_row: Node, OBJ_data , INT_index: number) =>
                {

                    $(OBJ_row).on("click", (OBJ_event) => {
                        this.rowClickHandler(OBJ_data);
                    });

                } // end function rowCallback
            };

		}
		else
		{

            let STR_searchfields = "",
                STR_sort_by = "",
                STR_order = "asc";

            for (let OBJ_column of ARR_columns_config) {

                // Suche nur ueber Spalten, die vom type string sind
                if (
                    !OBJ_column.type ||
                    (
                        OBJ_column.type &&
                        OBJ_column.type == "string"
                    )
                )
                {
                    STR_searchfields += OBJ_column.data + ",";
                } // end if

            } // end for

            // Letztes Komma entfernen
            STR_searchfields = STR_searchfields.slice(0, -1);

            // table configuration
            this.dt_options = {
                // Aktiviere serverseitige Paginierung, Sortierung und Suche
                serverSide: true,
                // Suche erst nach gewisser Wartezeit ( in ms)
                searchDelay: 1500,
                // Hier wird Anfrage an Server geschickt und Antwort verarbeitet
				ajax: (OBJ_input_data, callback, OBJ_settings) => {

					this.helper_service.logWithClassAndFunction("ListingComponent", "fillTableWithData", {"input_data": OBJ_input_data});

					if(OBJ_input_data['order'].length > 0)
					{
						let INT_order_by = OBJ_input_data['order'][0]['column'];

						if(ARR_columns_config[INT_order_by]['sort_by'])
						{
							STR_sort_by =  ARR_columns_config[INT_order_by]['sort_by'];
						}
						else
						{
							STR_sort_by =  ARR_columns_config[INT_order_by]['data'];
						} // end if

						STR_order =  OBJ_input_data['order'][0]['dir'];
					} // end if

					// Beim ersten Aufruf wurden die Daten bereits vom Resolver bereitgestellt
					if(OBJ_input_data['draw'] == 1)
					{

						// when user refreshes table manually content is already reloaded, dont take the content from the resolver
						if(!BOO_refresh)
						{
							this.contents_helper = this.activate_route.snapshot.data.OBJ_contents_helper;
						} // end if

						callback(
							{
								data: this.contents_helper.getListingData(),
								draw: OBJ_input_data['draw'],
								recordsTotal: this.contents_helper.getTotalNumberOfItems(),
								recordsFiltered: this.contents_helper.getTotalNumberOfItems()
							}
						);
					}
					else {

						let columns_search = "",
							columns_searchfields = "",
							columns_searchvalues = "";

						// Spalten selektieren, bei denen im Footer ein Suchwert eingetragen ist
						columns_search = OBJ_input_data['columns'].filter(item => item['search']['value'] != '' )

						// Spalten aufteilen in Fields und Values zur Übergabe an ElasticSearch
						for (let column of columns_search) {
							columns_searchfields += column['data'] + ",";
							columns_searchvalues += column['search']['value'].trim() + ",";
						}

						// Letztes Komma entfernen
						columns_searchfields = columns_searchfields.slice(0, -1);
						columns_searchvalues = columns_searchvalues.slice(0, -1);

						this.listing_service.getHelper(
							this.route_menu,
							this.route_submenu,
							this.listing_id,
							OBJ_input_data['length'],
							OBJ_input_data['start'],
							STR_sort_by,
							STR_order,
							OBJ_input_data['search']['value'],
							STR_searchfields,
							columns_searchfields,
							columns_searchvalues
						)
							.subscribe(
								OBJ_response => {
									this.contents_helper = OBJ_response;
									callback(
										{

											data: this.contents_helper.getListingData(),
											draw: OBJ_input_data['draw'],
											recordsTotal: this.contents_helper.getTotalNumberOfItems(),
											recordsFiltered: this.contents_helper.getTotalNumberOfItems()
										}
									);
								},
								OBJ_error => {
									console.log("error", OBJ_error);

								},
								() => {});

					}

				},
                // Konfiguration der Tabellenspalten
                columns: ARR_columns_config,
                // Vorgegebene Sortierung
                order: ARR_order_config,
                // Sortierbar?
                ordering: BOO_orderable,
				responsive: true,
                // Durchsuchbar?
                searching: (this.listing_config.searchable !== null)?this.listing_config.searchable:true,
                // Speichere den State des Datatables
                stateSave: true,
                // setzte State (Suchtext, order...) in localstorage
                stateSaveCallback:(settings: SettingsLegacy, data: StateReturnModel) => this.setTableState(data, STR_searchfields, STR_sort_by, STR_order),
                // Hole State des Datatables, falls gesetzt
                stateLoadCallback: settings => this.getTableState(),
                // Daten muessen nicht indiziert werden
                processing: false,
                language: {
                    url: "assets/i18n/datatables/" + this.translate_service.currentLang + ".json"
                },
                lengthMenu: [10, 25, 50, 100],
                pageLength: 25,
                // click event for the row
                rowCallback: (OBJ_row: Node, OBJ_data , INT_index: number) =>
                {

                    $(OBJ_row).on("click", (OBJ_event) => {
                        this.rowClickHandler(OBJ_data);
                    });

                } // end function rowCallback
            };
		} // end if

		// hide spinner
		// this.spinner_service.hide();

		// render table again
		this.rerenderTable();

	} // end fillTableWithData


	/**
	 * Function > rerenderTable
     *
	 * @desc Sorgt dafuer das neue Daten auch in View angezeigt werden -
	 * noetig da man immer in dieser Component landet und Angular so view nicht automatisch neu rendert
	 */
	rerenderTable()
	{

		this.table_show = false;
		this.change_detector_ref.detectChanges();
		this.table_show = true;

	} // end rerenderTable


    /**
	 * Function > reloadTableData
	 * @desc Holt die Tabellendaten der aktuellen Route nochmal neu
     */
	reloadTableData()
	{

        let OBJ_table_data = this.helper_service.getFromLocalstorage("table" + "/" + this.route_menu + "/" + this.route_submenu  + (this.listing_id?("/" + this.listing_id):""));

        this.helper_service.logWithClassAndFunction("ListingComponent", "reloadTableData", {"Table_data": OBJ_table_data});

		let Subscription_Helper =  this.listing_service.getHelper(this.route_menu, this.route_submenu, this.listing_id);

        // Sortierung etc. im Localstorage gespeichert
        if (OBJ_table_data)
        {
            Subscription_Helper =  this.listing_service.getHelper(this.route_menu, this.route_submenu, this.listing_id, OBJ_table_data.length, OBJ_table_data.start,
				OBJ_table_data.sort_by, OBJ_table_data.order_dir, OBJ_table_data.search.search, OBJ_table_data.searchfields);
        } // end if

        // Daten nochmal nachladen
        Subscription_Helper.subscribe(OBJ_helper => {
                this.contents_helper = OBJ_helper;
                this.fillTableWithData(true);
                // Tabelle nochmals rendern
                this.rerenderTable();
            });

	} // end function reloadTableData


    /**
	 * Function > createNewItem
	 * @desc Navigiert zur Seite zur Erstellung eines neun Items
	 * */
    createNewItem()
    {
		// No nested listing - navigate to normal new-page
		if(!this.nested_listing)
		{
			this.router.navigate([
				this.route_menu,
				this.route_submenu,
				"new"
			]);
		}
		else
		{

			// Neuen Eintrag im Unterleisting erzeugen
			if(this.listing_id)
			{
				this.router.navigate([
					this.route_menu,
					"listing",
					this.route_submenu,
					this.listing_id,
					"new"
				]);
			}
			else
			{
				this.router.navigate([
					this.route_menu,
					"listing",
					this.route_submenu,
					"new"
				]);
			} // end if

		} // end if

	} // end function createNewItem


	/**
	 * Function > createBulkinsert
	 * @desc Navigiert zur Seite zur Erstellung vieler neun Items
	 * */
	createBulkinsert()
	{
		this.router.navigate([
				this.route_menu,
				this.route_submenu,
				"new"
			],
			{queryParams: {bulk: "true"}}
		);

	} // end function createNewItem


    /**
     * Function > setTableState
     */
    setTableState(OBJ_data: StateReturnModel, STR_searchfields = "", STR_sort_by = "", STR_order = "")
    {

        // Fuege Felder, ueber die gesucht werdern soll, localstorage hinzu
        OBJ_data["searchfields"] = STR_searchfields;
        OBJ_data["sort_by"] = STR_sort_by;
        OBJ_data["order_dir"] = STR_order;

        // Setze Daten zum sortieren des Datatables in localstorage
        this.helper_service.setInLocalstorage(
            "table" + "/" + this.route_menu + "/" + this.route_submenu + (this.listing_id?("/" + this.listing_id):""),
            OBJ_data);

        this.helper_service.logWithClassAndFunction(
            "ListingComponent",
            "setTableState",
            {"Table_data": OBJ_data, "param": "table" + "/" + this.route_menu + "/" + this.route_submenu}
            );

    } // end setTableState


    /**
     * Function > getTableState
     */
    getTableState()
    {
        // Hole Daten aus localstorage
        let OBJ_return = this.helper_service.getFromLocalstorage(
            "table" + "/" + this.route_menu + "/" + this.route_submenu  + (this.listing_id?("/" + this.listing_id):"")
        );

        this.helper_service.logWithClassAndFunction(
            "ListingComponent",
            "getTableState",
            {"Table_data": OBJ_return, "param": "table" + "/" + this.route_menu + "/" + this.route_submenu  + (this.listing_id?("/" + this.listing_id):""),
            }
        );

		this.instantiateFilter();

        if(OBJ_return)
        {
            return OBJ_return;
        } // end if

        return {};
    } // end function getTableState


	/**
	 * Function > instantiateFilter
	 */
	instantiateFilter()
	{
		// Hängt Filterzeile am Tablehead an
		$('#filterrow').prependTo($('#datatable'));

		// Implement search for column filter
		this.datatableElement.dtInstance.then((dtInstance: DataTables.Api) => {
			dtInstance.columns().every(function () {

				const that = this;
				const column_idx = that.index();

				// Nur die Spalten abgleichen, bei denen Suchwerte eingetragen sind
				$('input', this.footer()).on('keyup change', function () {
					const input_idx = Number(this.closest('th').getAttribute('data-index'));
					if (column_idx == input_idx && that.search() !== this['value']) {
						that
							.search(this['value'])
							.draw();
					}
				});
			});
		});

		return {};
	} // end function instantiateFilter


	/**
	 * Function > goBack
	 * @desc Take a step back in the navigation
	 */
	goBack(): void
	{
		this.helper_service.goBack(this.router);
	} // end function goBack

} // end class ListingComponent
